"use client";

import Image from "next/image";
import { Card, cn, P2 } from "@focal/ui-kit";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDeviceOS } from "@/hooks/useDeviceOS";
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL } from "@/constants";

interface AppDownloadBannerProps {
  className?: string;
}

export function AppDownloadBanner({ className }: AppDownloadBannerProps) {
  const { isTablet } = useIsMobile();
  const { isIOS, isAndroid, isMobileDevice } = useDeviceOS();

  // QR code sizes - match the modal
  const qrCodeSize = isTablet ? 110 : 140;
  const badgeWidth = 120;
  const badgeHeight = 40;

  return (
    <Card
      className={cn(
        `bg-secondary border-0 rounded-xl p-6`,
        !isTablet && "mt-6",
        className,
      )}
      data-cy="app-download-banner"
    >
      <div className="flex flex-col items-center gap-4">
        {isMobileDevice ? (
          // Mobile view: Show app store badge images
          <div
            className="flex flex-col gap-4 items-center"
            data-cy="banner-mobile-buttons"
          >
            <p className="font-semibold text-base text-center leading-6 tracking-tight">
              For the best experience, download the mobile app for in-person
              meetings.
            </p>
            <P2 className="font-medium text-center">Click & download app</P2>
            {isIOS ? (
              <a
                href={IOS_APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                data-cy="banner-download-ios-button"
              >
                <Image
                  src="/onboarding/app-store1.svg"
                  alt="Download on the App Store"
                  width={badgeWidth}
                  height={badgeHeight}
                  priority
                />
              </a>
            ) : isAndroid ? (
              <a
                href={ANDROID_PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                data-cy="banner-download-android-button"
              >
                <Image
                  src="/onboarding/google-play1.svg"
                  alt="Get it on Google Play"
                  width={badgeWidth + 15}
                  height={badgeHeight}
                  priority
                />
              </a>
            ) : (
              // Unknown mobile device - show both
              <>
                <a
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  data-cy="banner-download-ios-button"
                >
                  <Image
                    src="/onboarding/app-store1.svg"
                    alt="Download on the App Store"
                    width={badgeWidth}
                    height={badgeHeight}
                    priority
                  />
                </a>
                <a
                  href={ANDROID_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  data-cy="banner-download-android-button"
                >
                  <Image
                    src="/onboarding/google-play1.svg"
                    alt="Get it on Google Play"
                    width={badgeWidth + 15}
                    height={badgeHeight}
                    priority
                  />
                </a>
              </>
            )}
          </div>
        ) : (
          // Desktop view: Show QR codes side by side
          <>
            <P2 className="font-semibold text-center">
              For the best experience, download the mobile app for in-person
              meetings.
            </P2>
            <div className="flex flex-row items-start justify-center gap-6">
              {/* iOS QR code */}
              <div className="flex flex-col items-center text-center gap-1">
                <div
                  className="bg-white rounded-lg flex items-center justify-center"
                  style={{ width: qrCodeSize, height: qrCodeSize }}
                >
                  <Image
                    src="/onboarding/ios.png"
                    alt="Scan to download Focal for iOS"
                    width={qrCodeSize - 16}
                    height={qrCodeSize - 16}
                    priority
                  />
                </div>
                <P2 className="font-medium mt-1">Scan for iOS</P2>
                <a
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <Image
                    src="/onboarding/app-store1.svg"
                    alt="Download on the App Store"
                    width={badgeWidth}
                    height={badgeHeight}
                    priority
                  />
                </a>
              </div>

              {/* Android QR code */}
              <div className="flex flex-col items-center text-center gap-1">
                <div
                  className="bg-white rounded-lg flex items-center justify-center"
                  style={{ width: qrCodeSize, height: qrCodeSize }}
                >
                  <Image
                    src="/onboarding/android.png"
                    alt="Scan to download Focal for Android"
                    width={qrCodeSize - 16}
                    height={qrCodeSize - 16}
                    priority
                  />
                </div>
                <P2 className="font-medium mt-1">Scan for Android</P2>
                <a
                  href={ANDROID_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <Image
                    src="/onboarding/google-play1.svg"
                    alt="Get it on Google Play"
                    width={badgeWidth + 15}
                    height={badgeHeight}
                    priority
                  />
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
